import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft, ArrowRight, Share2, Linkedin, Twitter, Facebook, Mail, Newspaper, Clock } from "lucide-react";
import api from "../lib/api";
import Reveal from "../components/Reveal";

function asUrl(u) {
  if (!u) return "";
  return u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

function readingMinutes(text = "") {
  const words = (text || "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

export default function NewsDetail() {
  const { id } = useParams();
  const [n, setN] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/news/${id}`).then((r) => setN(r.data)).catch(() => setN(false));
  }, [id]);

  useEffect(() => {
    if (!n) return;
    api.get("/news").then((r) => {
      setRelated(r.data.filter((x) => x.id !== n.id).slice(0, 3));
    });
  }, [n]);

  const rt = useMemo(() => readingMinutes(n?.content), [n]);

  if (n === null) {
    return <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-6 py-20 text-slate-500">Loading…</div>;
  }
  if (n === false) {
    return (
      <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-6 py-20 text-center">
        <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <div className="text-slate-500">News article not found.</div>
        <Link to="/news" className="inline-block mt-4 text-amber-600 font-bold text-sm uppercase tracking-wider">
          ← Back to all news
        </Link>
      </div>
    );
  }

  const dateStr = new Date(n.date).toLocaleDateString("en-IN", {
    day: "2-digit", month: "long", year: "numeric",
  });
  const banner = asUrl(n.image_url);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = encodeURIComponent(n.title);

  return (
    <article data-testid="news-detail-page" className="bg-white">
      {/* HERO BANNER */}
      <section className="relative bg-carbon-950 text-white overflow-hidden">
        {banner ? (
          <img
            src={banner}
            alt={n.title}
            className="absolute inset-0 w-full h-full object-cover opacity-40 ken-burns"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-b from-carbon-950/80 via-carbon-950/70 to-carbon-950" />
        <div className="absolute inset-0 bg-grid opacity-25" />
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-8 pt-8 md:pt-10 pb-14 md:pb-20">
          {/* Breadcrumb */}
          <div className="text-xs text-slate-400 font-mono mb-6 inline-flex items-center gap-2">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <span>/</span>
            <Link to="/news" className="hover:text-amber-400">Newsroom</Link>
            <span>/</span>
            <span className="text-amber-400 truncate max-w-[40ch]">{n.title}</span>
          </div>

          <Reveal>
            <div className="overline text-amber-400 mb-4 inline-flex items-center gap-3">
              <span className="h-px w-8 bg-amber-400" /> Newsroom
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.06] max-w-4xl text-white">
              {n.title}
            </h1>
            {n.summary ? (
              <p className="body-lead text-slate-300 mt-5 max-w-3xl">{n.summary}</p>
            ) : null}

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-300">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-amber-400" /> {dateStr}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-400" /> {rt} min read
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full pulse-dot" />
                <span className="font-mono uppercase tracking-widest">Universal EE Press</span>
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BANNER IMAGE FRAME (overlapping) */}
      {banner ? (
        <div className="relative bg-white">
          <div className="max-w-[1100px] mx-auto px-5 md:px-8 -mt-10 md:-mt-16 mb-10 md:mb-14">
            <div className="aspect-[16/9] bg-slate-100 border border-slate-200 overflow-hidden shadow-[0_30px_60px_-20px_rgba(10,19,34,0.35)]">
              <img src={banner} alt={n.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      ) : null}

      {/* CONTENT */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-8 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-12">
        {/* LEFT - sticky share */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="lg:sticky lg:top-28 flex flex-col items-center gap-3">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 font-mono [writing-mode:vertical-rl] rotate-180 mt-2">
              Share
            </div>
            <Share2 className="w-4 h-4 text-amber-500" />
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 text-slate-600 transition-colors rounded-full"
              aria-label="LinkedIn"
            ><Linkedin className="w-4 h-4" /></a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`}
              target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 text-slate-600 transition-colors rounded-full"
              aria-label="Twitter"
            ><Twitter className="w-4 h-4" /></a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 text-slate-600 transition-colors rounded-full"
              aria-label="Facebook"
            ><Facebook className="w-4 h-4" /></a>
            <a
              href={`mailto:?subject=${shareText}&body=${encodeURIComponent(shareUrl)}`}
              className="w-9 h-9 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 text-slate-600 transition-colors rounded-full"
              aria-label="Email"
            ><Mail className="w-4 h-4" /></a>
          </div>
        </aside>

        {/* MAIN ARTICLE */}
        <div className="lg:col-span-8 article-prose">
          {n.content ? (
            n.content.split(/\n+/).filter(Boolean).map((para, i) => (
              <p key={i} className="article-para">{para}</p>
            ))
          ) : (
            <p className="article-para text-slate-500">No content.</p>
          )}

          {/* Mobile share */}
          <div className="lg:hidden mt-10 pt-8 border-t border-slate-200">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Share this article</div>
            <div className="flex gap-2">
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 rounded-full"><Linkedin className="w-4 h-4" /></a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 rounded-full"><Twitter className="w-4 h-4" /></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 rounded-full"><Facebook className="w-4 h-4" /></a>
              <a href={`mailto:?subject=${shareText}&body=${encodeURIComponent(shareUrl)}`} className="w-10 h-10 grid place-items-center border border-slate-200 hover:border-amber-500 hover:text-amber-600 rounded-full"><Mail className="w-4 h-4" /></a>
            </div>
          </div>
        </div>

        {/* RIGHT — about / pull-quote */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-28">
            <div className="border-l-2 border-amber-500 pl-4 py-2 mb-6">
              <div className="overline text-amber-600 mb-2">About Universal EE</div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Founded 2016 in KG Halli, Bangalore — an Electronics R&amp;D and Mining Safety
                Equipment Trading &amp; Service company. Engineered for India&rsquo;s heavy-mining fleets.
              </p>
            </div>
            <Link
              to="/contact"
              className="block w-full bg-amber-500 hover:bg-amber-600 text-carbon-950 font-bold text-xs uppercase tracking-[0.18em] py-3 text-center transition-colors rounded-full"
              data-testid="news-contact-cta"
            >
              Talk to Sales
            </Link>
          </div>
        </aside>
      </section>

      {/* RELATED NEWS */}
      {related.length > 0 && (
        <section className="bg-slate-50 py-14 md:py-20 border-t border-slate-200">
          <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="overline mb-2">Keep reading</div>
                <h2 className="h-section text-carbon">More from the newsroom</h2>
              </div>
              <Link to="/news" className="text-sm font-bold uppercase tracking-[0.18em] text-carbon hover:text-amber-600 inline-flex items-center gap-2 group">
                All news <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((r, i) => (
                <Reveal key={r.id} delay={i * 80}>
                  <Link to={`/news/${r.id}`} data-testid={`related-news-${r.id}`} className="group block card-premium overflow-hidden bg-white h-full">
                    <div className="aspect-[16/9] bg-slate-100 zoom-wrap">
                      {r.image_url ? (
                        <img src={asUrl(r.image_url)} alt={r.title} className="w-full h-full object-cover zoom-img" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-slate-400 text-xs">No image</div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="font-mono text-xs text-amber-600 mb-2 inline-flex items-center gap-1.5 font-bold">
                        <Calendar className="w-3 h-3" />
                        {new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                      <h3 className="h-card text-carbon group-hover:text-amber-600 transition-colors line-clamp-2">{r.title}</h3>
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{r.summary}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/news" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-slate-700 hover:text-amber-600">
                <ArrowLeft className="w-4 h-4" /> Back to all news
              </Link>
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
